import React, { useCallback, useEffect, useState } from "react";
import { ConfigProvider, Form, Tooltip } from "antd";
import { toast } from "react-toastify";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Timeline from "@mui/icons-material/Timeline";
import Pause from "@mui/icons-material/Pause";
import PlayCircle from "@mui/icons-material/PlayCircle";
import Settings from "@mui/icons-material/Settings";
import WarningAmber from "@mui/icons-material/WarningAmber";
import FormBox from "../../Components/Form";
import StaticDropdown from "../../Components/Dropdown/StaticDropdown";
import TabBox from "../../Components/Tabbox/TabBox";
import TableButton from "../../Components/Button";
import KaminOrderForm, { NumericField } from "./KaminOrderForm";
import KaminSettingsModal from "./KaminSettingsModal";
import Modal from "../../Components/Modal/Modal";
import KaminOrderBooks from "./KaminOrderBooks";
import KaminTable, { KaminColumn } from "./KaminTable";
import KaminRowReports, { KaminRowReport } from "./KaminRowReports";
import "./Kamin2Leg.css";
import { getAllowedSahraAccounts } from "../../Requests/general";
import { IUserPortfoGroupSelectionRow } from "../../Interfaces/portfolioGroups";
import { bulkKaminState, changeKaminState, ExcelPreview, getKaminDefaults, getKaminInstruments, getKaminPage, getKaminTemplate,
  importKaminExcel, KaminCostParameters, KaminDefaults, KaminError, KaminInput, KaminOrder, KaminPreview, KaminTab, KaminTrade, previewKamin, previewKaminExcel, saveKaminDefaults, submitKamin } from "../../Requests/kamin2leg";
import { defaultKeys, newKaminInput, parameterFields, statusNames } from "./fields";
import { printPriceWithDecimalsForTableCell } from "../../helper";
import Cell from "../../Tables/TableTemplate/Cell";
import { toJalaliDateTime } from "../../Utils/date";
import { packetErrorHandler } from "../../Utils/requestUtils";
import FileInput from "../../Components/FileInput/FileInput";
import SahraConnections from "../Sahra/SahraConnections";
import NLegOrderPreviewTable from "../../Tables/NLegs/NLegOrderPreviewTable";
import FileBulkOrderTableUI from "../../Tables/Order/FileBulkOrder/FileBulkOrderTableUI";
import { INLegsAddOrderFormValues } from "../../Interfaces/nLegsInterfaces";
import { ErrorTableReport } from "../../Interfaces/orderInterfaces";
import { Report } from "../../Interfaces/TableInterfaces";

const number = printPriceWithDecimalsForTableCell;
const date = (v: string) => <Cell clonedRow={{ value: toJalaliDateTime(v) || "-" }} headerName="Value" type="Normal" />;
const notifyResult = (result: { success: boolean; message: string }) => {
  if (result.success) toast.success(result.message);
  else toast.error(result.message);
  return result.success;
};
type PreviewRow = INLegsAddOrderFormValues & { input: KaminInput };
const costFields: { title: string; key: keyof KaminCostParameters; maxKey?: keyof KaminCostParameters }[] = [
  { title: "بهای هدف", key: "targetMinCost", maxKey: "targetMaxCost" },
  { title: "بازه عدم حساسیت", key: "ignoreMinCost", maxKey: "ignoreMaxCost" },
  { title: "حداکثر ب.ت شکار", key: "huntMaxCost" },
  { title: "حداکثر ب.ت رفع ناترازی", key: "finalHedgeMaxCost" },
  { title: "سقف ناترازی (% حجم کل)", key: "maxImbalancePercent" },
];
const costColumns = (getParameters: (row: any) => KaminCostParameters | undefined): KaminColumn[] => costFields.map(field => ({
  title: field.title, render: (_, row) => {
    const parameters = getParameters(row);
    if (!parameters) return "—";
    const min = parameters[field.key];
    const max = field.maxKey ? parameters[field.maxKey] : min;
    return <div className="whitespace-nowrap"><Cell headerName="Value" type="Normal"
      clonedRow={{ value: field.key === "targetMinCost" || min !== max
        ? `${number(min)} — ${number(max)}` : number(min) }} /></div>;
  },
}));

export default function Kamin2Leg() {
  const [form] = Form.useForm<KaminInput>(); const [editForm] = Form.useForm<KaminInput>(); const [settingsForm] = Form.useForm<KaminDefaults>();
  const [defaults, setDefaults] = useState<KaminDefaults>();
  const [accounts, setAccounts] = useState<IUserPortfoGroupSelectionRow[]>([]);
  const [instruments, setInstruments] = useState<Awaited<ReturnType<typeof getKaminInstruments>>>([]);
  const [formOpen, setFormOpen] = useState(false); const [settingsOpen, setSettingsOpen] = useState(false);
  const [kaminSettingsOpen, setKaminSettingsOpen] = useState(false);
  const [editing, setEditing] = useState<KaminOrder>(); const [preview, setPreview] = useState<KaminPreview>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [focusedOrder, setFocusedOrder] = useState<KaminOrder>();
  const draftLegs = Form.useWatch("legs", form) || [];
  const [rowReport, setRowReport] = useState<KaminRowReport>();
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<KaminTab>("orders");
  const [accountFilter, setAccountFilter] = useState<number>(); const [instrumentFilter, setInstrumentFilter] = useState<string>();
  const [selected, setSelected] = useState<React.Key[]>([]); const [refresh, setRefresh] = useState(0);
  const [excelOpen, setExcelOpen] = useState(false); const [excelRows, setExcelRows] = useState<ExcelPreview[]>([]);
  const [excelResults, setExcelResults] = useState<{ id: number; success: boolean; message: string }[]>();
  const [excelFile, setExcelFile] = useState<FormData | null>(null);
  const [excelMode, setExcelMode] = useState<"preview" | "connections" | "results">("preview");

  useEffect(() => {
    Promise.all([getKaminDefaults(), getAllowedSahraAccounts(), getKaminInstruments()]).then(([d, a, i]) => {
      setDefaults(d); setAccounts(a); setInstruments(i.filter(x => x.type !== 4)); form.setFieldsValue(newKaminInput(d));
    }).catch(packetErrorHandler);
  }, [form]);
  const load = useCallback((offset: number, pageSize: number, signal: AbortSignal) => getKaminPage<any>(tab, { offset, pageSize,
    sahraUserId: accountFilter, instrumentId: instrumentFilter, includeCompleted: false }, signal),
    [tab, accountFilter, instrumentFilter]);
  const formAccounts = accounts.map(x => ({ id: x.id, name: `${x.accountName} (${x.groupName})` }));
  const instrumentName = (id: string | undefined, fallback: string) => instruments.find(x => x.id === id)?.name || fallback;
  const resetPage = () => { setSelected([]); };
  const focusOrder = (order: KaminOrder) => {
    form.setFieldsValue({
      ...order.input,
      criterionType: order.input.criterionType ?? "SignedCost",
      legs: order.input.legs.map(leg => ({ ...leg })),
    });
    setPreview(undefined);
    setFocusedOrder(order);
  };
  const openForm = (order?: KaminOrder) => {
    setEditing(order); setPreview(undefined);
    if (order) { editForm.resetFields(); editForm.setFieldsValue({ ...order.input, criterionType: order.input.criterionType ?? "SignedCost" }); setFormOpen(true); }
    else { form.resetFields(); form.setFieldsValue(newKaminInput(defaults!)); setFocusedOrder(undefined); }
  };
  const makePreview = async (input: KaminInput, order?: KaminOrder) => {
    setBusy(true);
    try {
      const validated = await previewKamin(input);
      if (order) {
        const result = await submitKamin(validated.input, order.id);
        if (!notifyResult(result)) return;
        setFormOpen(false); setEditing(undefined); setRefresh(x => x + 1);
      } else { setPreview(validated); setPreviewOpen(true); }
    } catch (e) { packetErrorHandler(e); } finally { setBusy(false); }
  };
  const act = async (action: "pause" | "play" | "delete", order?: KaminOrder) => {
    setBusy(true);
    try {
      if (order) notifyResult(await changeKaminState(order.id, action));
      else {
        const results = await bulkKaminState(selected.map(Number), action);
        const successes = Array.from(new Set(results.filter(x => x.success).map(x => x.message)));
        const failures = Array.from(new Set(results.filter(x => !x.success).map(x => x.message)));
        if (successes.length) toast.success(successes.join("؛ "));
        if (failures.length) toast.error(failures.join("؛ "));
      }
      setRefresh(x => x + 1); setSelected([]);
    } catch (e) { packetErrorHandler(e); } finally { setBusy(false); }
  };
  const download = async () => {
    try { const blob = await getKaminTemplate(); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "kamin2leg.xlsx"; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); }
    catch (e) { packetErrorHandler(e); }
  };
  const previewValue: PreviewRow[] | null = preview ? [{
    input: preview.input, userId: preview.input.legs[0].sahraUserId,
    uNLegsItems: preview.input.legs.map((leg, i) => ({ instrumentId: leg.instrumentId, orderSide: leg.side,
      optionOrderType: leg.optionOrderType, ratio: leg.ratio, volume: preview.legVolumes[i],
      contractSize: instruments.find(x => x.id === leg.instrumentId)?.contractSize || 1 })),
    maxPairPosition: preview.input.targetMaxCost, blockSizePercent: null, marginPercent: null,
  }] : null;
  const validExcelRows = excelRows.filter(x => x.preview && !x.error);
  const closeExcel = () => {
    if (busy) return;
    if (excelMode === "connections") setExcelMode("preview");
    else setExcelOpen(false);
  };
  const previewExcel = async () => {
    const file = excelFile?.get("file");
    if (!(file instanceof File)) return;
    setBusy(true);
    try {
      setExcelRows(await previewKaminExcel(file)); setExcelResults(undefined);
      setExcelMode("preview"); setExcelOpen(true);
    } catch (e) { packetErrorHandler(e); } finally { setBusy(false); }
  };
  const sendExcel = async () => {
    setBusy(true);
    try {
      const results = await importKaminExcel(validExcelRows.map(x => x.preview!.input));
      setExcelResults(results); setRefresh(x => x + 1);
      if (results.some(x => !x.success)) setExcelMode("results");
      else {
        Array.from(new Set(results.map(x => x.message))).forEach(msg => toast.success(msg));
        setExcelOpen(false); setExcelFile(null); setExcelRows([]);
      }
    } catch (e) { packetErrorHandler(e); } finally { setBusy(false); }
  };
  const excelResultReport: Report<ErrorTableReport> = {
    report: (excelResults || []).map((result, index) => ({ id: index + 1, row: validExcelRows[index]?.row || index + 1,
      username: Array.from(new Set(validExcelRows[index]?.preview?.input.legs.map(leg => leg.sahraUserId) || []))
        .map(id => accounts.find(account => account.id === id)?.accountName || id).join(" / "),
      itemError: { row: validExcelRows[index]?.row || index + 1, msg: result.message }, orderId: result.id,
      status: result.success ? "موفق" : "ناموفق", message: result.message,
    })),
    translatedHeaders: [{ headerName: "Row", translateHeaderName: "ردیف", typeStr: "Normal", positionTypeStr: "FirstPage" },
      { headerName: "Username", translateHeaderName: "اکانت", typeStr: "Normal", positionTypeStr: "FirstPage" },
      { headerName: "OrderId", translateHeaderName: "سفارش", typeStr: "Normal", positionTypeStr: "FirstPage" },
      { headerName: "Status", translateHeaderName: "وضعیت", typeStr: "Normal", positionTypeStr: "FirstPage" },
      { headerName: "Message", translateHeaderName: "پیام", typeStr: "Normal", positionTypeStr: "FirstPage" }],
  };
  const reportButtons = (orderId: number, graph = false) => <>
    {(graph ? ["chances", "broker-orders"] : ["broker-orders", "chances"]).map(kind =>
      <TableButton key={kind} text={kind === "chances" ? "شانس ها" : "سفارشات"} variant="contained" className="table-btn"
        onClick={() => setRowReport({ orderId, kind: kind as "chances" | "broker-orders" })} />)}
    {graph && <span title="نمودار حجم انجام‌شده"><TableButton text={<Timeline titleAccess="نمودار" className="!text-base" />} variant="contained"
      className="table-btn" onClick={() => setRowReport({ orderId, kind: "graph" })} /></span>}
  </>;
  const ordersColumns = [
    { title: "#", dataIndex: "id" },
    { title: "اکانت", render: (_: any, o: KaminOrder) => Array.from(new Set(o.input.legs.map(l => l.sahraUserId))).map(id =>
      <div key={id}>{accounts.find(a => a.id === id)?.accountName || id}</div>) },
    { title: "نماد", render: (_: any, o: KaminOrder) => o.names.map((n, i) => <div key={i}>{instrumentName(o.input.legs[i]?.instrumentId, n)}</div>) },
    { title: "نماد کمین", render: (_: any, o: KaminOrder) => {
      const index = o.input.kaminLegIndex;
      const name = instrumentName(o.input.legs[index]?.instrumentId, o.names[index] || "—");
      return <div className="whitespace-nowrap"><Cell headerName="Value" type="Normal"
        clonedRow={{ value: name }} /></div>;
    } },
    { title: "جهت", render: (_: any, o: KaminOrder) => o.input.legs.map((l, i) => <div key={i} className={l.side === 1 ? "text-green-700" : "text-red-700"}>{l.side === 1 ? "خرید" : "فروش"}</div>) },
    { title: "نسبت", render: (_: any, o: KaminOrder) => o.input.legs.map((l, i) => <div key={i}>{number(l.ratio)}</div>) },
    { title: "offset", render: (_: any, o: KaminOrder) => o.input.legs.map((l, i) => <div key={i}>{l.optionOrderType === 1 ? "Offset" : "Open"}</div>) },
    { title: "حجم کل", render: (_: any, o: KaminOrder) => number(o.input.totalVolume) },
    { title: "انجام شده", render: (_: any, o: KaminOrder) => <div className="whitespace-nowrap">
      <Cell clonedRow={{ value: `${number(o.filledUnits1)} _ ${number(o.filledUnits2)}` }} headerName="Value" type="Normal" /></div> },
    { title: "باز", render: (_: any, o: KaminOrder) => <div className="whitespace-nowrap">
      <Cell clonedRow={{ value: `${number(o.openUnits1)} _ ${number(o.openUnits2)}` }} headerName="Value" type="Normal" /></div> },
    ...costColumns((o: KaminOrder) => o.input),
    { title: "ناترازی / سقف", render: (_: any, o: KaminOrder) => <>{number(o.imbalanceUnits)} / {number(o.input.totalVolume * o.input.maxImbalancePercent / 100)}</> },
    { title: "حجم بلوک", render: (_: any, o: KaminOrder) => number(o.input.blockVolume) },
    { title: "تاریخ", dataIndex: "createdAt", render: date },
    { title: "وضعیت", render: (_: any, o: KaminOrder) => <div className="w-full">
      <Cell clonedRow={{ value: o.status === "Editing" ? statusNames[o.isActive ? "Active" : "Paused"]
        : statusNames[o.status] || o.status }} headerName="Value" type="Normal" />
      {o.message && <div className="kamin-warning">{o.message}</div>}</div> },
    { title: "هشدار", render: (_: any, o: KaminOrder) => o.warning
      ? <Tooltip title={<span dir="rtl" className="whitespace-pre-wrap">{o.warning}</span>}>
        <span tabIndex={0} aria-label="هشدار" className="inline-flex items-center text-amber-600 cursor-help">
          <WarningAmber fontSize="small" />
        </span>
      </Tooltip> : "—" },
  ];
  const orderActions = [
    { key: "edit", render: (o: KaminOrder) =>
      <button className="hover:bg-slate-300 transition-all rounded p-1 text-sky-700 disabled:text-gray-300" aria-label="ویرایش" title="ویرایش" disabled={busy || !!o.deletedAt} onClick={() => openForm(o)}><Edit className="cursor-pointer" /></button> },
    { key: "state", header: <div className="flex flex-row">
      <button className="hover:bg-slate-200 transition-all rounded p-1 text-sky-900 disabled:text-gray-300" aria-label="Pause انتخاب‌شده‌ها" title="توقف انتخاب‌شده‌ها" disabled={busy || !selected.length} onClick={() => act("pause")}><Pause /></button>
      <button className="hover:bg-slate-200 transition-all rounded p-1 text-sky-900 disabled:text-gray-300" aria-label="Play انتخاب‌شده‌ها" title="ادامه انتخاب‌شده‌ها" disabled={busy || !selected.length} onClick={() => act("play")}><PlayCircle /></button>
      </div>, render: (o: KaminOrder) =>
      <button className="hover:bg-slate-300 transition-all rounded p-1 text-sky-700 disabled:text-gray-300" aria-label={o.isActive ? "Pause" : "Play"} title={o.isActive ? "توقف" : "ادامه"}
        disabled={busy || !!o.deletedAt} onClick={() => act(o.isActive ? "pause" : "play", o)}>{o.isActive ? <Pause className="cursor-pointer" /> : <PlayCircle className="cursor-pointer" />}</button> },
    { key: "chances", render: (o: KaminOrder) => <TableButton text="شانس ها" variant="contained" className="table-btn"
      onClick={() => setRowReport({ orderId: o.id, kind: "chances" })} /> },
    { key: "monitoring", render: (o: KaminOrder) => <TableButton text="کمین" variant="contained" className="table-btn"
      onClick={() => setRowReport({ orderId: o.id, kind: "monitoring" })} /> },
    { key: "broker-orders", render: (o: KaminOrder) => <TableButton text="سفارشات" variant="contained" className="table-btn"
      onClick={() => setRowReport({ orderId: o.id, kind: "broker-orders" })} /> },
    { key: "graph", render: (o: KaminOrder) => <TableButton text={<Timeline titleAccess="نمودار" className="!text-base" />}
      variant="contained" className="table-btn" onClick={() => setRowReport({ orderId: o.id, kind: "graph" })} /> },
    { key: "delete", header:
      <button className="hover:bg-red-100 transition-all rounded p-1 text-red-900 disabled:text-gray-300" aria-label="Delete انتخاب‌شده‌ها" title="کنسل انتخاب‌شده‌ها" disabled={busy || !selected.length} onClick={() => act("delete")}><Delete fontSize="small" /></button>,
      render: (o: KaminOrder) => <button className="hover:bg-red-100 transition-all rounded p-1 text-red-700 disabled:text-gray-300" aria-label="Delete" title="کنسل" disabled={busy} onClick={() => act("delete", o)}><Delete fontSize="small" className="cursor-pointer" /></button> },
  ];
  const tradeColumns = [
    { title: "شناسه", dataIndex: "id" }, { title: "سفارش", dataIndex: "orderId" }, { title: "لگ", dataIndex: "legIndex", render: (v: number) => v + 1 },
    { title: "نماد", render: (_: any, trade: KaminTrade) => instrumentName(trade.instrumentId, trade.instrumentName || trade.instrumentId || "—") },
    { title: "قیمت واقعی", dataIndex: "price", render: number }, { title: "حجم نماد", dataIndex: "volume", render: number },
    { title: "واحد بسته", dataIndex: "units", render: number }, { title: "واحد رفع‌نشده", dataIndex: "unmatchedUnits", render: number }, { title: "زمان", dataIndex: "executedAt", render: date },
    ...costColumns((trade: KaminTrade) => trade.parameters),
    { title: "عملیات", render: (_: any, trade: { orderId: number }) => <div className="kamin-row-actions">{reportButtons(trade.orderId)}</div> },
  ];
  const errorsColumns: KaminColumn[] = [
    { title: "شناسه", dataIndex: "id" }, { title: "سفارش", dataIndex: "orderId" },
    { title: "نام اکانت", render: (_, error: KaminError) => accounts.find(account => account.id === error.sahraUserId)?.accountName || error.accountName || "—" },
    { title: "نام نماد", render: (_, error: KaminError) => instrumentName(error.instrumentId, error.instrumentName || error.instrumentId || "—") },
    { title: "تعداد", dataIndex: "volume", render: number },
    { title: "تاریخ سفارش", dataIndex: "createdAt", render: date },
    { title: "وضعیت", dataIndex: "status" }, { title: "پیام", dataIndex: "message" },
    { title: "زمان خطا", dataIndex: "updatedAt", render: date },
  ];

  const tabKeys: KaminTab[] = ["orders", "trades", "errors"];
  return <ConfigProvider direction="rtl"><div className="kamin-layout flex flex-row justify-start items-start gap-5 w-full pr-5 outline-none border-none">
    <aside className="kamin-sidebar flex flex-col justify-start items-center gap-5">
      <KaminOrderForm form={form} accounts={formAccounts} instruments={instruments} busy={busy} ready={!!defaults}
        onPreview={input => makePreview(input)}
        onChange={() => { setPreview(undefined); setFocusedOrder(undefined); }} />
      <FormBox>
        <div className="p-2 w-full">
          <div className="bold mb-2 text-xs">ارسال دسته ای</div>
          <FileInput fileExtension=".xlsx" UI={<></>} onChange={setExcelFile} value={excelFile}
            name="file" label="انتخاب فایل اکسل" />
          <div className="mt-3 w-full"><TableButton variant="contained" text="ارسال" loading={busy}
            disabled={!excelFile || busy} onClick={previewExcel} /></div>
          <div className="mt-3 w-full"><TableButton variant="outlined" text="اکسل نمونه" onClick={download} /></div>
        </div>
      </FormBox>
    </aside>
    <main className="kamin-main flex flex-col justify-start items-start">
      <div className="flex flex-row justify-start items-start w-full gap-8">
        <KaminOrderBooks legs={focusedOrder?.input.legs || draftLegs} />
      </div>
      <div className="kamin-tables">
        <div className="flex flex-row flex-wrap justify-start items-center gap-3 w-full">
          <div className="max-w-min shrink-0"><TabBox activeTab={tabKeys.indexOf(tab)}
            onChange={(_, index) => { setTab(tabKeys[index]); resetPage(); }} children={[
              { index: 0, title: "سفارشات" }, { index: 1, title: "معاملات" }, { index: 2, title: "خطا" },
            ]} /></div>
          <button className="flex items-center gap-1 text-[10px] text-slate-500 shrink-0" disabled={!defaults}
            onClick={() => { settingsForm.setFieldsValue(defaults!); setSettingsOpen(true); }}>
            <Settings className="!text-[15px]" />تنظیمات پیش‌فرض</button>
          <button className="flex items-center gap-1 text-[10px] text-slate-500 shrink-0" disabled={!defaults || busy}
            onClick={() => setKaminSettingsOpen(true)}><Settings className="!text-[15px]" />تنظیمات کمین</button>
          <div className="kamin-filters">
            <div><StaticDropdown dropdown={{ name: "account", placeholder: "اکانت" }} options={formAccounts} value={accountFilter}
              onChange={(_, v) => { setAccountFilter(v == null ? undefined : Number(v)); resetPage(); }} /></div>
            <div><StaticDropdown dropdown={{ name: "instrument", placeholder: "نماد" }} options={instruments} value={instrumentFilter}
              onChange={(_, v) => { setInstrumentFilter(v || undefined); resetPage(); }} /></div>
          </div>
          <div className="kamin-utilities">
            {focusedOrder && <button onClick={() => setFocusedOrder(undefined)}>اردربوک فرم</button>}
          </div>
        </div>
        <div className="w-full mt-2"><KaminTable key={`${tab}-${accountFilter}-${instrumentFilter}`} columns={tab === "orders" ? ordersColumns : tab === "trades" ? tradeColumns : errorsColumns}
          getPage={load} refreshKey={refresh} selected={tab === "orders" ? selected : undefined} onSelect={setSelected}
          onRowClick={tab === "orders" ? focusOrder : undefined}
          actionColumns={tab === "orders" ? orderActions : undefined} /></div>
      </div>
    </main>
    {formOpen && <Modal modalBoxClassName="kamin-edit-modal" onClose={() => !busy && setFormOpen(false)} UI={
      <KaminOrderForm form={editForm} accounts={formAccounts} instruments={instruments} editing={editing} busy={busy} ready={!!defaults}
        onPreview={input => makePreview(input, editing)} onChange={() => setPreview(undefined)}
        onCancel={() => { setFormOpen(false); setEditing(undefined); }} />
    } />}
    {previewOpen && <Modal onClose={() => !busy && setPreviewOpen(false)} UI={
      <div className="flex flex-col justify-start items-center gap-8">
      <NLegOrderPreviewTable values={previewValue} instrumentOptions={instruments} showContractSize
        getAccountIds={value => Array.from(new Set((value as PreviewRow).input.legs.map(leg => leg.sahraUserId)))}
        onRemoveRow={() => setPreview(undefined)}
        costColumns={[...(preview?.input.legs.some(leg => leg.volume != null) ? [
          { title: "حجم پایه", render: (value: INLegsAddOrderFormValues) => number(value.uNLegsItems[0].volume) },
          { title: "حجم دوم", render: (value: INLegsAddOrderFormValues) => number(value.uNLegsItems[1].volume) },
        ] : [{ title: "حجم کل بسته", render: (value: INLegsAddOrderFormValues) => number((value as PreviewRow).input.totalVolume) }]),
          ...costFields.map(field => ({ title: field.title, render: (value: INLegsAddOrderFormValues) => {
          const input = (value as PreviewRow).input; const min = input[field.key]; const max = field.maxKey ? input[field.maxKey] : min;
          return field.maxKey ? `${number(min)} — ${number(max)}` : number(min);
        } }))]} />
      <TableButton variant="contained" text="ارسال" loading={busy} disabled={!previewValue?.length} onClick={async () => {
        if (!preview) return;
        setBusy(true); try { if (!notifyResult(await submitKamin(preview.input))) return;
          setPreviewOpen(false); setPreview(undefined); setRefresh(x => x + 1); }
        catch (e) { packetErrorHandler(e); } finally { setBusy(false); }
      }} /></div>
    } />}
    {settingsOpen && <Modal modalBoxClassName="compact-confirm-modal border border-gray-200 shadow-xl"
      onClose={() => !busy && setSettingsOpen(false)} UI={<FormBox className="!w-full !border-0 gap-3 !p-0">
      <div className="text-xs font-bold w-full border-b border-gray-200 pb-2">تنظیمات پیش‌فرض</div>
      <Form form={settingsForm} component={false} onFinish={async values => {
        const numericValues = Object.fromEntries(defaultKeys.map(key => [key, Number(values[key])])) as unknown as KaminDefaults;
        setBusy(true); try { setDefaults(await saveKaminDefaults(numericValues)); setSettingsOpen(false); toast.success("پیش‌فرض‌ها ذخیره شدند"); }
        catch (e) { packetErrorHandler(e); } finally { setBusy(false); }
      }}>{parameterFields.filter(f => defaultKeys.includes(f.key as keyof KaminDefaults)).map(f => <Form.Item noStyle key={f.key} name={f.key} rules={[{ required: true }]}>
        <NumericField name={f.key} label={f.label} integer disabled={busy} /></Form.Item>)}
        <TableButton variant="contained" text="ذخیره" loading={busy} onClick={() => settingsForm.submit()} /></Form>
    </FormBox>} />}
    {kaminSettingsOpen && <KaminSettingsModal onClose={() => setKaminSettingsOpen(false)} onSaved={() => setRefresh(x => x + 1)} />}
    {rowReport && <KaminRowReports key={`${rowReport.kind}-${rowReport.orderId}`} report={rowReport} instrumentName={instrumentName}
      accountName={id => accounts.find(account => account.id === id)?.accountName || "—"} onClose={() => setRowReport(undefined)} />}
    {excelOpen && <Modal onClose={closeExcel} UI={excelMode === "connections" ?
      <div className="flex flex-col justify-start items-center gap-3">
        <SahraConnections initialSearchParam={{ userIds: Array.from(new Set(validExcelRows.flatMap(x => x.preview!.input.legs.map(leg => leg.sahraUserId)))) }} />
        <TableButton variant="contained" text="ارسال" loading={busy} disabled={busy} onClick={sendExcel} />
      </div> : excelMode === "results" ? <FileBulkOrderTableUI reports={excelResultReport} /> :
      <div className="flex flex-col justify-between items-center h-full">
      <KaminTable rows={excelRows} onSelect={() => {}} columns={[
        { title: "ردیف", dataIndex: "row" }, { title: "خطا", dataIndex: "error" },
        { title: "اکانت", render: (_: any, r: ExcelPreview) => r.preview
          ? Array.from(new Set(r.preview.input.legs.map(leg => leg.sahraUserId))).map(id => accounts.find(account => account.id === id)?.accountName || id).join(" / ") : "—" },
        { title: "نماد", render: (_: any, r: ExcelPreview) => r.preview?.instrumentNames.map((n, i) => <div key={i}>{instrumentName(r.preview!.input.legs[i]?.instrumentId, n)}</div>) },
        { title: "نسبت", render: (_: any, r: ExcelPreview) => r.preview?.input.legs.map((leg, i) => <div key={i}>{number(leg.ratio)}</div>) },
        ...parameterFields.map(f => ({ title: f.label, render: (_: any, r: ExcelPreview) => r.preview ? number(r.preview.input[f.key]) : "—" })),
      ]} />
        <div className="mt-3 w-full"><TableButton variant="contained" text="ارسال" disabled={!validExcelRows.length || busy}
          onClick={() => setExcelMode("connections")} /></div>
    </div>} />}
  </div></ConfigProvider>;
}
